import { cn } from "@/lib/utils";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { bucketUrlPrefix, chatAssistantEnabled } from "common/config";
import { getVenueLocationSubtitle, pinTypeForVenueDetails } from "common/utils";
import {
  VenueDetailsBySlugResponse,
  useAuth,
  useDeleteReview,
  useFilters,
  useI18N,
  useSavedPlaces,
  useSyncBusinessInterest,
} from "common/hooks";
import { Storage } from "common/storage";
import { useNearbyBusinesses } from "common/hooks/business/useNearbyBusinesses";
import Modal from "react-responsive-modal";
import FocusTrap from "focus-trap-react";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { BusinessPhoto, VenueReview } from "common/types";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
  venueAssistantDockOpenAtom,
} from "@/store/atoms";
import {
  ArrowLeftIcon,
  CaretRightIcon,
  PlusCircleIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useCustomToast } from "@/hooks/useCustomToast";
import ModalWithAction from "@/components/common/ModalWithAction";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";
import VenueImageWithActions from "./VenueImageWithActions";
import VenueStatsRow from "./VenueStatsRow";
import { countVenuePhotos } from "./venuePhotoCount";
import { ContributionOptionsModal } from "@/components/Map/ContributionOptionsModal";
import type { ContributionAction } from "@/components/Map/PoiContributionCard";
import OpeningHours from "@/components/VenueCard/TodaysOpeningHours";
import AccessibilitySection from "./AccessibilitySection";
import PhotosTab from "./PhotosTab";
import AlertsTab from "./AlertsTab";
import ReviewsTab from "./ReviewsTab";
import AboutTab from "./AboutTab";
import VenueAssistantTab from "./VenueAssistantTab";
import FlagVenueReview from "@/features/flag-report/FlagVenueReview";
import EditVenueNote from "@/components/VenueDetailBasic/Feature/EditVenueNote";
import FlagVenueNote from "@/features/flag-report/FlagVenueNote";
import AddMapperNote from "@/components/VenueDetailBasic/Feature/AddMapperNote";
import AreaDetailsNotes from "@/components/VenueDetailBasic/Feature/AreaDetailsNotes";
import { AddReviewModal } from "@/features/add-review/AddReviewModal";
import VenueInfoClaim from "@/components/VenueDetailBasic/Feature/VenueInfoClaim";
import { DirectionsLink } from "@/components/VenueDetailBasic/DirectionsLink";
import { VenueDataSourceBadge } from "@/components/VenueCard/VenueDataSourceBadge";
import ScrollContainer from "react-indiana-drag-scroll";
import { RowsPhotoAlbum } from "react-photo-album";
import FullImageViewer from "@/components/FullImageViewer";

import Loader from "@/components/common/Loader";
import { InterestNotificationModal } from "../Notifications/NotificationModal";
import { createPageAnalytics, trackEvent } from "common/analytics";
import { trackContributeCta } from "@/analytics/contributeAnalytics";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import { DESKTOP_LAYOUT_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";
import VenueAboutBlock from "./VenueAboutBlock";

// A review's internal `accessibility_experience` is "cool" / "weird"; the canonical analytics
// `experience_type` value is "sweet" / "sour" (cool = sweet, weird = sour — mirrors the UI label).
// Returns "" when there's no experience so we never report a spurious "sour".
const experienceTypeForAnalytics = (
  accessibilityExperience?: string | null,
): string =>
  !accessibilityExperience
    ? ""
    : accessibilityExperience === "cool"
      ? "sweet"
      : "sour";

type VenueSheetContentProps = {
  onBack: () => void;
  venueDetailsResponse: VenueDetailsBySlugResponse;
  fullSize?: boolean;
  onRefresh: () => void;
  openAllPhotosInline: boolean;
  onOpenAllPhotos?: () => void;
  cid?: string;
  tid?: string;
  currentVenueTypePage?: number;
  onShowDetailView?: (initialSpaceId: string) => void;
  onShowPhotosDetailView?: (initialCategoryId: number | null) => void;
  tb?: string;
  tab?: string;
  openModal?: boolean;
  notify?: boolean;
  source?: "map" | "activity" | "notifications-page" | "notification-settings" | "places";
  /** False until the visitor's real location is known — then the distance is meaningful. */
  showDistance?: boolean;
  /** The venue page's "About this place" block; off on the map and list panels. */
  showAboutBlock?: boolean;
};

function getFivePhotos(businessPhotos: BusinessPhoto[]): BusinessPhoto[] {
  // Separate photos by user role priority
  const priorityPhotos: BusinessPhoto[] = []; // isGeneralUser: false
  const generalPhotos: BusinessPhoto[] = []; // isGeneralUser: true

  // Categorize photos by user role
  for (const photo of businessPhotos) {
    if (photo.user_roles?.isGeneralUser === false) {
      priorityPhotos.push(photo);
    } else {
      generalPhotos.push(photo);
    }
  }

  const uniqueTagPhotosMap = new Map<string, BusinessPhoto>();
  const result: BusinessPhoto[] = [];

  // First, populate with priority photos (isGeneralUser: false) ensuring unique tagNames
  for (const photo of priorityPhotos) {
    if (!uniqueTagPhotosMap.has(photo.tagName)) {
      uniqueTagPhotosMap.set(photo.tagName, photo);
    }
    if (uniqueTagPhotosMap.size >= 5) {
      break;
    }
  }

  // If we still need more photos, add from general photos (isGeneralUser: true)
  if (uniqueTagPhotosMap.size < 5) {
    for (const photo of generalPhotos) {
      if (!uniqueTagPhotosMap.has(photo.tagName)) {
        uniqueTagPhotosMap.set(photo.tagName, photo);
      }
      if (uniqueTagPhotosMap.size >= 5) {
        break;
      }
    }
  }

  // Add unique photos to the result
  const uniquePhotos = Array.from(uniqueTagPhotosMap.values());
  result.push(...uniquePhotos);

  // If we have fewer than 5 unique photos, add more photos without duplicating
  // First try priority photos, then general photos
  const remainingPhotos = [...priorityPhotos, ...generalPhotos];

  for (const photo of remainingPhotos) {
    if (result.length >= 5) {
      break;
    }
    if (!result.includes(photo)) {
      result.push(photo);
    }
  }

  return result;
}

function VenueSheetContent({
  onBack,
  venueDetailsResponse,
  fullSize = false,
  onRefresh,
  openAllPhotosInline,
  cid,
  tid,
  currentVenueTypePage,
  onShowDetailView,
  onShowPhotosDetailView,
  tb,
  tab,
  openModal,
  notify,
  source,
  showDistance = true,
  showAboutBlock = false,
}: VenueSheetContentProps) {
  const i18n = useI18N();
  // One layout per render: the server and hydration render mobile, so the SSR HTML
  // carries a single copy of the venue and one <h1>.
  const isDesktopLayout = useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const showToast = useCustomToast();
  const { deleteReviewMutation } = useDeleteReview();

  // Debug: log the source prop
  // console.log("VenueSheetContent - source prop:", source);
  const venueDetails = venueDetailsResponse.details;
  const accessibilityCategories = venueDetailsResponse.accesibilityCategories;
  const isTemporarilyClosed =
    venueDetails.businessStatus === "CLOSED_TEMPORARILY";
  // Read only in the desktop column, which mounts after hydration, so the clock-based
  // hours never reach the server HTML from here.
  const showTodaysHours =
    !isTemporarilyClosed && venueDetails.openingHours?.[0]?.mode === 0;

  const locationOnly = getVenueLocationSubtitle({
    sublocality: venueDetails.sublocality,
    route: venueDetails.route,
    postalTown: venueDetails.postalTown,
    locality: venueDetails.locality,
    cityLocation: venueDetails.cityLocation,
  });

  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const [currentReviewSort, setCurrentReviewSort] = useState("Latest");
  const [reviews, setReviews] = useState(venueDetails.businessReviews);
  const [areaDetailsOpenModal, setAreaDetailsOpenModal] = useState(false);

  // Update reviews when venueDetails changes (e.g., after refetch from adding a review)
  useEffect(() => {
    setReviews(venueDetails.businessReviews);
  }, [venueDetails.businessReviews]);

  const { showModal } = useAuthModals();

  const { isLoggedIn, user } = useAuth();
  const showMapperView =
    isLoggedIn &&
    (user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);

  const [isFlagReviewOpen, setIsFlagReviewOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [addNoteOpenModal, setAddNoteOpenModal] = useState(false);

  const [editVenueNoteOpenModal, setEditVenueNoteOpenModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [flagVenueNoteOpenModal, setFlagVenueNoteOpenModal] = useState(false);
  const [flagNote, setFlagNote] = useState(null);
  const [isAddReviewOpen, setIsAddReviewOpen] = useState(false);
  // DEV-544 — edit/delete your own experience. `reviewToEdit` doubles as the modal's
  // mode switch; `reviewToDelete` holds the confirmation's subject.
  const [reviewToEdit, setReviewToEdit] = useState<VenueReview | null>(null);
  const [reviewToDelete, setReviewToDelete] = useState<VenueReview | null>(null);
  // Desktop "Contribute information" opens the same picker the map's venue
  // snippet uses for "Add info".
  const [isContributeOpen, setIsContributeOpen] = useState(false);

  // We are already on the venue page, so the picker's actions resolve in place
  // rather than navigating. Only "share experience" is wired on desktop —
  // adding photos is mobile-only, and alerts are not part of this revision.
  const handleContributionAction = (action: ContributionAction) => {
    if (action !== "experiences") return;
    setIsContributeOpen(false);
    if (!isLoggedIn) {
      handleShowLoginModal();
      return;
    }
    trackEvent("share_experience_form_opened", {
      business_id: venueDetails?.business?.id?.toString() ?? "",
      brand_id: venueDetails?.brandId != null ? String(venueDetails.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      place_name: venueDetails?.name ?? "",
    });
    setIsAddReviewOpen(true);
  };
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [preSelectedNotificationType, setPreSelectedNotificationType] =
    useState<"accessibility" | "liveAlerts" | null>(null);

  // Mobile-only full-screen Ask AI overlay (below md, where no map column exists).
  const [isAssistantOverlayOpen, setIsAssistantOverlayOpen] = useState(false);

  // On md+ the tab-bar "Ask AI" button docks the assistant over the page's map
  // column (shared atom drives the dock rendered by each page). Mobile uses the
  // teaser card → full-screen overlay instead.
  const [isAssistantDockOpen, setAssistantDockOpen] = useAtom(venueAssistantDockOpenAtom);
  const openAssistant = () => {
    setAssistantDockOpen(true);
  };
  // Single entry point for callers (e.g. the accessibility tab's "Ask
  // Sociability Bot") that don't already know which surface applies —
  // docked at md+, the same full-screen overlay as the mobile teaser below.
  const openAssistantAnywhere = () => {
    if (typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches) {
      openAssistant();
    } else {
      setIsAssistantOverlayOpen(true);
    }
  };

  const { isLoading: isSyncingInterest } = useSyncBusinessInterest();

  // Generate accessibility alerts from businessNotesAll
  const accessibilityAlerts = useMemo(() => {
    return (
      venueDetails.businessNotesAll
        ?.filter((note) => note.status === "Open")
        .map((note) => ({
          id: note.business_note_text_id,
          dateRange:
            note.formatedScheduleDateFrom && note.formatedScheduleDateTo
              ? `${note.formatedScheduleDateFrom} to ${note.formatedScheduleDateTo}`
              : "",
          flaggedDate: `Added on ${note.formatedCreatedAt} by ${note.business_note_by_user}`,
          title: note.business_note_title,
          location: note.location_name,
          description: note.business_note_text,
        })) || []
    );
  }, [venueDetails.businessNotesAll]);

  // Add nearby businesses query
  const { data: nearbyBusinessesData, isLoading: isLoadingNearbyBusinesses } =
    useNearbyBusinesses(venueDetails.business.id.toString());

  const [fullImageView, setFullImageView] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [photoDimensions, _setPhotoDimensions] = useState<{
    [key: string]: { width: number; height: number };
  }>({});

  const [sortByText, setSortByText] = useState("Date added (Newest)");

  const [_showInlinePhotos] = useState(false);

  // Add hasNearbyVenues constant
  const hasNearbyVenues = useMemo(() => {
    if (isLoadingNearbyBusinesses) return false;
    return (
      venueDetails.templateType !== "Basic" &&
      nearbyBusinessesData?.data?.nearbyBusinessCategories?.length > 0
    );
  }, [
    isLoadingNearbyBusinesses,
    venueDetails.templateType,
    nearbyBusinessesData,
  ]);

  const hasAccessibilityData =
    !hasNearbyVenues || accessibilityCategories?.length > 0;

  const isLoadingTabs = isLoadingNearbyBusinesses;

  const [showReviewsTab] = useState(true);
  const [showNotesTab] = useState(true);

  // Initial render uses the default tab — sessionStorage is read in the
  // useEffect below so the initial value is identical on server and client
  // (avoids SSR ReferenceError and hydration mismatch).
  const [selectedMainTab, setSelectedMainTab] = useState<
    "accessibility" | "photos" | "alerts" | "reviews" | string
  >("accessibility");

  // Ref to track if modal was already auto-opened from query param
  const hasAutoOpenedModalRef = useRef(false);

  // Refs for tab buttons to enable keyboard navigation
  const desktopTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const mobileTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabContentScrollRef = useRef<HTMLDivElement>(null);
  // Below lg, the bordered tab-content div never itself overflows — it grows
  // to fit its content and this outer wrapper is the actual scroller.
  const pageScrollRef = useRef<HTMLDivElement>(null);
  const mobileTabBarRef = useRef<HTMLDivElement>(null);

  // On tab change, put the newly selected tab's content in view.
  // Desktop: the panel has its own scroller, so resetting it is enough.
  // Below lg: the whole page scrolls and the tab bar sits ~800px down, so
  // resetting to 0 would leave the content off-screen — align the tab bar to
  // the top of the scroller instead. On lg+ the outer wrapper is
  // overflow-hidden, so that second call is a no-op there.
  const revealSelectedTab = () => {
    tabContentScrollRef.current?.scrollTo({ top: 0 });
    const scroller = pageScrollRef.current;
    const bar = mobileTabBarRef.current;
    if (!scroller || !bar) return;
    const delta =
      bar.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTo({ top: scroller.scrollTop + delta });
  };

  // Every user-initiated tab change goes through here — the tab bar, keyboard
  // arrows, and the stats-row cells. Switching the tab without revealing it
  // leaves the new tab's content below the fold on mobile, so it reads as
  // nothing having happened.
  const selectMainTab = (tab: string) => {
    setSelectedMainTab(tab);
    revealSelectedTab();
  };

  // Store tab selection in sessionStorage whenever it changes
  useEffect(() => {
    if (venueDetails?.id && selectedMainTab) {
      sessionStorage.setItem(
        `selectedMainTab_${venueDetails.id}`,
        selectedMainTab,
      );
    }

    // Cleanup: Remove stored state when component unmounts
    return () => {
      // Note: We intentionally keep the state in sessionStorage even on unmount
      // so it persists during page refresh. It will be cleared when browser tab closes.
    };
  }, [selectedMainTab, venueDetails?.id]);

  // Handle back navigation with tab reset
  const handleBack = useCallback(() => {
    // Clear the stored tab for this venue so it resets to accessibility on next visit
    if (venueDetails?.id) {
      sessionStorage.removeItem(`selectedMainTab_${venueDetails.id}`);
    }
    // back_action_button -> place_closed (stub).
    trackEvent("place_closed", {});
    onBack();
  }, [venueDetails?.id, onBack]);

  const backLabel =
    source === "notifications-page" || String(source) === "notifications-page"
      ? i18n.get("backToAllNotifications") || "Back to all notifications"
      : source === "notification-settings" ||
          String(source) === "notification-settings"
        ? i18n.get("backToSettings") || "Back to settings"
        : source === "places"
          ? i18n.get("backToPlaces")
          : currentVenueTypePage === 1
            ? i18n.get("backToAllResults")
            : i18n.get("explore");

  useEffect(() => {
    switch (currentReviewSort) {
      case "Latest":
        setSortByText("Date added (Newest)");
        break;
      case "Earliest":
        setSortByText("Date added (Oldest)");
        break;
      case "Alphabetical":
        setSortByText("Alphabetical");
        break;
      default:
        setSortByText("Date added (Newest)");
    }
  }, [currentReviewSort]);

  // Tracks the (venueId | tb | tab) combination that was last initialised so we
  // can skip re-running the default-tab logic on plain data refreshes (e.g. the
  // post-login refetch that happens when the accessToken changes the query key).
  // Without this, a user who clicked into the Photos tab would be snapped back to
  // the accessibility tab every time venue data refetches.
  const tabInitKeyRef = useRef<string | null>(null);

  // Update selected tab when loading completes (but respect stored tab if it exists).
  // Two guards prevent spurious tab resets on post-login data refreshes:
  //   1. `&& venueDetails` — skips the run while data is transiently undefined
  //      (query-key change when the auth token becomes available).
  //   2. `tabInitKeyRef` — skips re-running for the same venue+params combination,
  //      so a tab the user manually clicked is not overwritten by a background fetch.
  useEffect(() => {
    if (!isLoadingTabs && venueDetails) {
      const currentKey = `${venueDetails.id}|${tb ?? ""}|${tab ?? ""}`;
      if (tabInitKeyRef.current === currentKey) {
        return; // Same venue + same URL params — don't reset a user-chosen tab.
      }
      tabInitKeyRef.current = currentKey;

      const storedTab = sessionStorage.getItem(
        `selectedMainTab_${venueDetails?.id}`,
      );

      // If tab param is explicitly passed, override stored tab preference
      if (tab === "reviews") {
        setSelectedMainTab("reviews");
      } else if (tab) {
        // Handle other tab values if needed in the future
        setSelectedMainTab(tab);
      } else if (storedTab) {
        setSelectedMainTab(storedTab);
      } else {
        // Only set default tab if no stored tab exists and no tab param
        // If tb=photo is passed, default to photos tab
        if (tb === "photo" && venueDetails?.businessPhotos?.length > 0) {
          setSelectedMainTab("photos");
        } else if (hasAccessibilityData) {
          setSelectedMainTab("accessibility");
        } else if (venueDetails?.businessPhotos?.length > 0) {
          setSelectedMainTab("photos");
        } else if (venueDetails?.businessNotesAll?.length > 0) {
          setSelectedMainTab("alerts");
        } else {
          setSelectedMainTab("reviews");
        }
      }
    }
  }, [isLoadingTabs, hasAccessibilityData, venueDetails, tb, tab]);

  // Auto-open AddReviewModal when openModal param is true and reviews tab is active
  useEffect(() => {
    if (
      openModal &&
      !isLoadingTabs &&
      selectedMainTab === "reviews" &&
      venueDetails &&
      !hasAutoOpenedModalRef.current
    ) {
      // Arriving with ?openModal=true (from the home/search "Add info" card) auto-opens the Share
      // Experience form — instrument it like the manual Share CTA. place_experience_share_cta_activated
      // fires regardless of auth (it gates the form); share_experience_form_opened only when the form
      // actually opens (logged in). Ref-guarded below, so each fires once (StrictMode-safe).
      createPageAnalytics("place").track("share_experience_action_button", {
        business_id: venueDetails.business.id.toString(),
        brand_id: venueDetails.brandId != null ? String(venueDetails.brandId) : "",
        brand_name: venueDetails.brandName ?? "",
        entry_page: entryPageFromCurrentPage(),
        place_name: venueDetails.name,
      });
      if (isLoggedIn) {
        trackEvent("share_experience_form_opened", {
          business_id: venueDetails.business.id.toString(),
          brand_id: venueDetails.brandId != null ? String(venueDetails.brandId) : "",
          brand_name: venueDetails.brandName ?? "",
          place_name: venueDetails.name,
        });
        setIsAddReviewOpen(true);
      } else {
        // Record intent so the form re-opens after login (param/ref are cleared below).
        setActionToPerformAfterLogin("addReview");
        handleShowLoginModal();
      }

      // Mark that we've auto-opened the modal
      hasAutoOpenedModalRef.current = true;

      // Clear the openModal query param to prevent reopening after submission
      // Preserve all other query params
      navigate({
        to: `/venuedetails/${venueDetails.business.slug}`,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: {
          ...(source && { source }),
          ...(cid && { cid }),
          ...(tid && { tid }),
          ...(tb && { tb }),
          ...(tab && { tab }),
        } as any,
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, isLoadingTabs, selectedMainTab, venueDetails, isLoggedIn]);

  // Determine which tabs to show
  const hasPhotos = venueDetails?.businessPhotos?.length > 0;
  const hasAlerts = venueDetails?.businessNotesAll?.length > 0;
  const hasReviews = venueDetails?.businessReviews?.length > 0;

  type TabItem = { id: string; label: string; badgeCount: number };

  // CheckIcon for dynamic tabs from API response
  const dynamicTabs = useMemo(() => {
    return venueDetails.businessTabs || [];
  }, [venueDetails.businessTabs]);

  // Build tabs array dynamically
  const mainTabItems: TabItem[] = [];

  if (hasAccessibilityData) {
    mainTabItems.push({
      id: "accessibility",
      label: i18n.get("accessibility"),
      badgeCount: 0,
    });
  }
  if (hasAlerts || showNotesTab) {
    mainTabItems.push({
      id: "alerts",
      label: i18n.get("alerts"),
      badgeCount: venueDetails?.businessNotesAll?.length ?? 0,
    });
  }
  if (hasPhotos) {
    mainTabItems.push({
      id: "photos",
      label: i18n.get("photos"),
      badgeCount: countVenuePhotos(venueDetails?.businessPhotos),
    });
  }
  if (hasReviews || showReviewsTab) {
    mainTabItems.push({
      id: "reviews",
      label: i18n.get("experiences"),
      badgeCount: venueDetails?.businessReviews?.length ?? 0,
    });
  }

  mainTabItems.push({ id: "about", label: i18n.get("about"), badgeCount: 0 });

  if (dynamicTabs.length > 0) {
    dynamicTabs.forEach((tab) => {
      mainTabItems.push({
        id: `dynamicTab_${tab.id}`,
        label: tab.title,
        badgeCount: 0,
      });
    });
  }

  const mainTabs = mainTabItems.map((t) => t.id);

  const [_initialSection, setInitialSection] = useState("All");
  const [hasProcessedCidForTabSwitch, setHasProcessedCidForTabSwitch] =
    useState(false);

  // Update initial section when cid or accessibilityCategories change
  useEffect(() => {
    if (cid && accessibilityCategories && accessibilityCategories.length > 0) {
      const category = accessibilityCategories.find(
        (cat) => cat.catId === parseInt(cid),
      );
      if (category) {
        setInitialSection(category.catName);

        // Auto-switch to accessibility tab and scroll to it (only once). An explicit
        // ?tab= wins: the photos tab reads cid/tid itself to land on a space.
        if (
          hasAccessibilityData &&
          !hasProcessedCidForTabSwitch &&
          (!tab || tab === "accessibility")
        ) {
          setSelectedMainTab("accessibility");
          setHasProcessedCidForTabSwitch(true);

          // Scroll to accessibility section after tab switch
          setTimeout(() => {
            const accessibilitySection = document.querySelector(
              '[data-section="accessibility"]',
            );
            console.log(
              "Looking for accessibility section:",
              accessibilitySection,
            );
            if (accessibilitySection) {
              console.log("Found accessibility section, scrolling to it");
              accessibilitySection.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            } else {
              console.log(
                "Accessibility section not found, trying alternative approach",
              );
              // Try scrolling to the main content area
              const mainContent =
                document.querySelector(".content-area") ||
                document.querySelector("main") ||
                document.body;
              if (mainContent) {
                mainContent.scrollTo({
                  top: 800, // Scroll down to show accessibility section
                  behavior: "smooth",
                });
              }
            }
          }, 500); // Increased delay to ensure tab switch completes
        }
      }
    } else {
      setInitialSection("All");
    }
  }, [
    cid,
    tab,
    accessibilityCategories,
    hasAccessibilityData,
    hasProcessedCidForTabSwitch,
  ]);

  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom(actionToPerformValueAfterLoginAtom);

  const isDark = useDarkMode();

  // Venue image logic (same as VenueCard)
  const { businessTypesFrontend } = useFilters();

  // Get photo from businessPhotoFirst or first item in businessPhotos array
  let businessPhoto = "";
  if (venueDetails?.businessPhotoFirst?.path) {
    businessPhoto = bucketUrlPrefix + venueDetails.businessPhotoFirst.path;
  } else if (venueDetails?.businessPhotos?.[0]?.path) {
    businessPhoto = bucketUrlPrefix + venueDetails.businessPhotos[0].path;
  }

  const businessphotoThumb = businessPhoto;
  const hasVenuePhoto = businessPhoto !== "";

  // Save venue state and hooks
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const [listNameCreated, setListNameCreated] = useState("");

  const {
    refetch: refetchSavedPlaces,
    isLoading: isSavedPlacesLoading,
    isFetching: isSavedPlacesFetching,
    data: savedPlacesData,
  } = useSavedPlaces();
  const savedPlaces = useMemo(
    () => savedPlacesData?.saved_places || [],
    [savedPlacesData?.saved_places],
  );

  useEffect(() => {
    if (selectedMainTab === "reviews") {
      if (currentReviewSort === "Latest") {
        reviews.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
        setReviews([...reviews]);
      } else if (currentReviewSort === "Earliest") {
        reviews.sort(
          (a, b) =>
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
        );
        setReviews([...reviews]);
      } else if (currentReviewSort === "Alphabetical") {
        reviews.sort((a, b) => {
          const nameA = a.notes.toUpperCase();
          const nameB = b.notes.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        setReviews([...reviews]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReviewSort]);

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      // Clear only inside the matched branch — never unconditionally — so this
      // watcher can't swallow a pending action that belongs to another surface
      // (photo flag, accessibility flag, etc.).
      if (actionToPerformAfterLogin === "addReview") {
        setSelectedMainTab("reviews");
        setIsAddReviewOpen(true);
        setActionToPerformAfterLogin("");
        setActionToPerformValueAfterLogin("");
      } else if (actionToPerformAfterLogin === "flagReview") {
        setSelectedMainTab("reviews");
        setSelectedReview(actionToPerformValueAfterLogin);
        setIsFlagReviewOpen(true);
        setActionToPerformAfterLogin("");
        setActionToPerformValueAfterLogin("");
      } else if (actionToPerformAfterLogin === "openNotificationModal") {
        setIsNotificationModalOpen(true);
        setActionToPerformAfterLogin("");
        setActionToPerformValueAfterLogin("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const top5Photos = getFivePhotos(venueDetails.businessPhotos);

  const handleShowLoginModal = () => {
    if (
      matchRoute({
        to: `/whats-nearby/$venueId`,
      })
    ) {
      showModal(AuthModalScreen.EnterEmail);
      return;
    }

    // CheckIcon if the current route matches the target route
    // if venue layer redirect to venue detail and then open login modal
    // if venue detail only then no redirect needed
    if (
      !matchRoute({
        to: `/venuedetails/${venueDetails.business.slug}`,
      })
    ) {
      navigate({
        to: `/venuedetails/${venueDetails.business.slug}`,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: { source: "map", showAuthModal: true } as any,
      });
    }

    showModal(AuthModalScreen.EnterEmail);
  };

  // Keyboard navigation handler for tabs
  const handleTabKeyDown = (
    e: React.KeyboardEvent,
    currentIndex: number,
    tabRefsArray: React.RefObject<(HTMLButtonElement | null)[]>,
  ) => {
    let newIndex = currentIndex;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        newIndex = (currentIndex + 1) % mainTabs.length;
        break;
      case "ArrowLeft":
        e.preventDefault();
        newIndex = (currentIndex - 1 + mainTabs.length) % mainTabs.length;
        break;
      case "Home":
        e.preventDefault();
        newIndex = 0;
        break;
      case "End":
        e.preventDefault();
        newIndex = mainTabs.length - 1;
        break;
      default:
        return;
    }

    // Activate the new tab
    selectMainTab(mainTabs[newIndex]);
    handleTabTracking(mainTabs[newIndex]);

    // Focus the new tab using the correct ref array
    tabRefsArray.current[newIndex]?.focus();
  };

  // Save venue handlers
  const handleCreateList = (listNameCreated: string) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  const handleSavedPlacesModalClose = () => {
    console.log("Closing Modal handleSavedPlacesModalClose");
    setOpenSavedPlacesModal(false);
    if (savePlaceListMode === "create") {
      handleSaveVenue();
    } else {
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  const handleSaveVenue = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetchSavedPlaces();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  // save_place_action_button -> place_save_cta_activated. Wraps handleSaveVenue so it fires only on
  // the user's save CTA (the heart), not the programmatic re-entries (post-login / modal re-open).
  const handleSavePlaceCta = () => {
    createPageAnalytics("place").track("save_place_action_button", {
      business_id: venueDetails?.business?.id?.toString() ?? "",
      brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      entry_page: entryPageFromCurrentPage(),
      pin_type: pinTypeForVenueDetails(venueDetails),
      place_name: venueDetails?.name ?? "",
    });
    handleSaveVenue();
  };

  const isVenueSaved = useCallback(() => {
    if (isSavedPlacesFetching || isSavedPlacesLoading) {
      return false;
    }
    if (savedPlaces) {
      const flattened = savedPlaces
        ?.map((e) => e.saved_place_details.map((e) => e.business.id))
        .flat();
      const result = flattened.some((id) => id === venueDetails?.business.id);
      return result;
    }
    return false;
  }, [
    isSavedPlacesFetching,
    isSavedPlacesLoading,
    savedPlaces,
    venueDetails?.business.id,
  ]);

  // Handle save venue action after login
  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin === "handleSaveVenue") {
      handleSaveVenue();
      setActionToPerformAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const handleFlagVenueReviewClose = (isSuccess?: boolean) => {
    // Only the user-dismiss path (X/backdrop/Esc) fires _closed. A successful submit
    // passes isSuccess and fires experience_flagged instead — avoid double-counting.
    if (!isSuccess) {
      trackEvent("flag_experience_form_closed", {
        business_id: venueDetails.business.id.toString(),
        brand_id: venueDetails.brandId != null ? String(venueDetails.brandId) : "",
        brand_name: venueDetails.brandName ?? "",
        experience_type: experienceTypeForAnalytics(
          selectedReview?.accessibility_experience,
        ),
        place_name: venueDetails.name,
      });
    }
    setIsFlagReviewOpen(false);
  };

  const handleAddNoteModalClose = () => {
    setAddNoteOpenModal(false);
  };

  const handleAddReviewClose = (isSuccess?: boolean) => {
    // Only the user-dismiss path (X/backdrop/Esc) fires _closed. A successful submit
    // passes isSuccess and fires experience_shared instead — avoid double-counting.
    // An edit is a different action from sharing, and ships without its own events,
    // so it stays out of the share funnel entirely.
    if (!isSuccess && !reviewToEdit) {
      trackEvent("share_experience_form_closed", {
        business_id: venueDetails.business.id.toString(),
        brand_id: venueDetails.brandId != null ? String(venueDetails.brandId) : "",
        brand_name: venueDetails.brandName ?? "",
        place_name: venueDetails.name,
      });
    }
    setIsAddReviewOpen(false);
    setReviewToEdit(null);
  };

  const handleDeleteExperience = () => {
    const review = reviewToDelete;
    setReviewToDelete(null);
    if (!review) return;

    // The card disappears via the hook's cache patch, so there is nothing to do here
    // on success beyond confirming it.
    deleteReviewMutation(
      {
        reviewId: review.id.toString(),
        accessToken: Storage.getCurrentUserToken() || "",
      },
      {
        onSuccess: () => showToast(i18n.get("experienceDeleted"), "success"),
        onError: (deleteError: Error) =>
          showToast(
            deleteError.message || i18n.get("couldNotDeleteExperience"),
            "error",
          ),
      },
    );
  };

  const handleNotificationClick = (
    preSelectType?: "accessibility" | "liveAlerts",
  ) => {
    // NB: place_notification_cta_activated is fired by each CTA at its OWN source element —
    // bell → VenueImageWithActions ("bell_action_button"), "I want to know" → AccessibilitySection
    // and the empty-state button below ("I_want_to_know_link_button"), live alerts → VenueMapperNotes
    // ("subscribe_live_alerts_action_button"). Do NOT fire it here: this is the shared
    // open-the-modal chokepoint, so firing here too double-counts every CTA.
    if (!isLoggedIn) {
      // Set action to perform after login
      setActionToPerformAfterLogin("openNotificationModal");
      // Show login modal
      showModal(AuthModalScreen.EnterEmail);
    } else {
      // Store which type to pre-select if provided
      if (preSelectType) {
        setPreSelectedNotificationType(preSelectType);
      }
      setIsNotificationModalOpen(true);
    }
  };

  const handleNotificationModalClose = () => {
    setIsNotificationModalOpen(false);
    setPreSelectedNotificationType(null);
  };

  // Auto-open the notification modal when arriving via the map "Get notified"
  // CTA (?notify=true). handleNotificationClick gates login itself.
  const hasAutoNotifyRef = useRef(false);
  useEffect(() => {
    if (notify && !isLoadingTabs && venueDetails && !hasAutoNotifyRef.current) {
      hasAutoNotifyRef.current = true;
      handleNotificationClick("accessibility");
      navigate({
        to: `/venuedetails/${venueDetails.business.slug}`,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: {
          ...(source && { source }),
          ...(cid && { cid }),
          ...(tid && { tid }),
          ...(tb && { tb }),
          ...(tab && { tab }),
        } as any,
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notify, isLoadingTabs, venueDetails]);

  const handleNotificationModalSuccess = () => {
    // The query will auto-refetch due to invalidation in useSyncBusinessInterest
    // We can optionally trigger a manual refetch as well, but it's not strictly necessary
    // Use a small delay to ensure the mutation completes before refetching
    setTimeout(() => {
      onRefresh();
    }, 300);
  };

  const handleNotificationModalComplete = () => {
    // Called when the success modal closes - trigger a refresh to update the UI
    setTimeout(() => {
      onRefresh();
    }, 100);
  };

  // Get current user's interest data
  const currentUserInterest = useMemo(() => {
    if (!user?.id || !venueDetails.businessInterestData) {
      return null;
    }
    return (
      venueDetails.businessInterestData.find(
        (interest) => interest?.user?.id === user?.id,
      ) || null
    );
  }, [user?.id, venueDetails.businessInterestData]);

  const isNotificationEnabled = () => {
    return (
      currentUserInterest &&
      (currentUserInterest.notify_accessibility_information === 1 ||
        currentUserInterest.notify_live_alert === 1)
    );
  };

  // Separate notification state checkers for each component
  const isAccessibilityInfoNotificationEnabled = () => {
    return (
      currentUserInterest &&
      currentUserInterest.notify_accessibility_information === 1
    );
  };

  const isLiveAlertNotificationEnabled = () => {
    return currentUserInterest && currentUserInterest.notify_live_alert === 1;
  };

  const handleTabTracking = (selectedTab) => {
    console.log("Tab changed to:", selectedTab);

    // Literal values (not Record<string,string>) so `elementKey` narrows to the
    // place page's element-key union that createPageAnalytics().track expects.
    const tabElementKey = {
      accessibility: "accessibility_tab",
      alerts: "alerts_tab",
      photos: "photos_tab",
      reviews: "experiences_tab",
      about: "about_tab",
    } as const;
    const tabInfoType: Record<string, string> = {
      accessibility: "accessibility",
      alerts: "alerts",
      photos: "photos",
      reviews: "experiences",
      about: "about",
    };
    const elementKey = tabElementKey[selectedTab as keyof typeof tabElementKey];
    if (elementKey) {
      createPageAnalytics("place").track(elementKey, {
        business_id: venueDetails.business.id.toString(),
        guide_name: "",
        interaction_method: "click",
        place_header_name: "",
        place_info_type: tabInfoType[selectedTab] ?? selectedTab,
        place_name: venueDetails.name,
        previous_place_header_name: "",
        previous_place_info_type:
          tabInfoType[selectedMainTab] ?? selectedMainTab,
      });
    }
  };

  const handleReviewSort = (value) => {
    console.log("handleReviewSort value", value);

    setCurrentReviewSort(value as any);
  };

  const showEditNote = (note: any) => {
    setEditVenueNoteOpenModal(true);
    setEditNote(note);
  };

  const showFlagNote = (note: any) => {
    console.log("showFlagNote", note);
    // alert_flag_cta_activated (the alert-flag CTA; flag_alert_form_opened fires separately on the
    // FlagVenueNote mount). Use the trackEvent primitive — the `place.flag_action_button` element
    // is overloaded (fans out to alert_flag_cta_activated + flag_experience_form_opened), so
    // createPageAnalytics would also fire the experience event here.
    trackEvent("alert_flag_cta_activated", {
      alert_title: note?.action_title ?? "",
      brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      business_id:
        venueDetails?.business?.id != null
          ? String(venueDetails.business.id)
          : "",
      place_name: venueDetails?.name ?? "",
    });
    setFlagVenueNoteOpenModal(true);
    setFlagNote(note);
  };

  const handleOpenFullImage = (image) => {
    setCurrentImage(image);
    setFullImageView(true);
  };

  useEffect(() => {
    // Add Summernote CSS
    const style = document.createElement("style");
    style.innerHTML = `
 .summernote-content {
 /* Base styles */
 font-family: inherit;
 line-height: 1.5;
 color: inherit;
 }
 
 /* Headings — match Notion editor; force color so global/theme heading
    tokens don't leak through. */
 .summernote-content h1, .summernote-content h2, .summernote-content h3,
 .summernote-content h4, .summernote-content h5, .summernote-content h6 {
 margin-top: 0.5em;
 margin-bottom: 0.5em;
 font-weight: 700;
 line-height: 1.3;
 color: inherit;
 letter-spacing: normal;
 font-family: inherit;
 }
 .summernote-content h1 { font-size: 26px; }
 .summernote-content h2 { font-size: 20px; font-weight: 700; }
 .summernote-content h3 { font-size: 18px; font-weight: 600; }
 .summernote-content h4 { font-size: 16px; font-weight: 600; }
 .summernote-content h5, .summernote-content h6 { font-size: 1rem; font-weight: 600; }
 
 /* Lists */
 .summernote-content ul, .summernote-content ol {
 padding-left: 2em;
 margin-top: 0.5em;
 margin-bottom: 0.5em;
 }
 .summernote-content ul { list-style-type: disc; }
 .summernote-content ol { list-style-type: decimal; }
 .summernote-content ul ul { list-style-type: circle; }
 .summernote-content ul ul ul { list-style-type: square; }
 
 /* Paragraphs and spacing */
 .summernote-content p {
 margin-bottom: 0.75em;
 }
 
 /* Links */
 .summernote-content a {
 color: #0066cc;
 text-decoration: underline;
 }
 
 /* Tables */
 .summernote-content table {
 border-collapse: collapse;
 width: 100%;
 margin: 1em 0;
 }
 .summernote-content th, .summernote-content td {
 border: 1px solid #ddd;
 padding: 8px;
 text-align: left;
 }
 
 /* Blockquotes */
 .summernote-content blockquote {
 margin: 1em 0;
 padding: 0.5em 1em;
 border-left: 5px solid #eee;
 color: #666;
 }
 
 /* Code blocks */
 .summernote-content pre {
 background-color: #f5f5f5;
 padding: 1em;
 border-radius: 4px;
 overflow-x: auto;
 }
 .summernote-content code {
 font-family: monospace;
 background-color: #f5f5f5;
 padding: 0.2em 0.4em;
 border-radius: 3px;
 }
 
 /* Images — cap, don't stretch. Default = medium (560px) when data-size
    is absent; S/M/L presets from the CMS editor override via attribute. */
 .summernote-content img {
 max-width: min(100%, 560px);
 width: auto;
 height: auto;
 display: block;
 }
 .summernote-content img[data-size="s"] { max-width: min(100%, 280px); }
 .summernote-content img[data-size="m"] { max-width: min(100%, 560px); }
 .summernote-content img[data-size="l"] { max-width: 100%; }
 .summernote-content img[data-align="left"] { margin-left: 0; margin-right: auto; }
 .summernote-content img[data-align="center"] { margin-left: auto; margin-right: auto; }
 .summernote-content img[data-align="right"] { margin-left: auto; margin-right: 0; }
 
 /* Alignment classes that Summernote uses */
 .summernote-content .text-left { text-align: left; }
 .summernote-content .text-center { text-align: center; }
 .summernote-content .text-right { text-align: right; }
 .summernote-content .text-justify { text-align: justify; }
 
 /* Float classes */
 .summernote-content .float-left {
 float: left;
 margin-right: 1em;
 margin-bottom: 0.5em;
 }
 .summernote-content .float-right {
 float: right;
 margin-left: 1em;
 margin-bottom: 0.5em;
 }
 
 /* Ensure proper spacing between elements */
 .summernote-content * + h1,
 .summernote-content * + h2,
 .summernote-content * + h3,
 .summernote-content * + h4,
 .summernote-content * + h5,
 .summernote-content * + h6 {
 margin-top: 1em;
 }`;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Function to handle dynamic tab content
  const renderDynamicTabContent = (tabId: string) => {
    const id = parseInt(tabId.split("_")[1]);
    const tab = dynamicTabs.find((t) => t.id === id);

    if (!tab) return null;

    const orderedBlocks = (tab.blocks ?? [])
      .slice()
      .sort((a, b) => a.order - b.order);

    // Flat list of every image across blocks — drives the lightbox so
    // navigation works across block boundaries.
    const allImages = orderedBlocks.flatMap((block) =>
      (block.images ?? []).slice().sort((a, b) => a.order - b.order),
    );

    return (
      <div className="flex flex-col">
        <h2 className="sr-only">{tab.title}</h2>
        <div className="mt-2">
          {/* Cover photo banner */}
          {tab.cover_photo && (
            <div className="w-full h-[220px] md:h-[320px] rounded-lg overflow-hidden mb-4">
              <img
                alt={tab.cover_photo_alt_text || tab.title}
                className="w-full h-full object-cover"
                src={bucketUrlPrefix + tab.cover_photo}
                loading="lazy"
              />
            </div>
          )}

          {/* Tab-level description */}
          {tab.description && (
            <p className="label-m-medium text-fg-neutral-strong mb-6">
              {tab.description}
            </p>
          )}

          {orderedBlocks.map((block) => {
            const blockImages = (block.images ?? [])
              .slice()
              .sort((a, b) => a.order - b.order);

            const BlockTag = block.title ? "section" : "div";
            return (
              <BlockTag key={block.id} className="mb-8" aria-labelledby={block.title ? `block-${block.id}` : undefined}>
                {block.title && (
                  <h3 id={`block-${block.id}`} className="title-s-bold text-fg-neutral-strong mb-3">
                    {block.title}
                  </h3>
                )}

                {block.content && (
                  <div
                    className="cms-block-content label-m-medium pb-4 dynamic-tab-content"
                    dangerouslySetInnerHTML={{ __html: block.content }}
                  />
                )}

                {blockImages.length > 0 && (
                  <div className="photo-grid-container relative mt-4">
                    <RowsPhotoAlbum
                      photos={blockImages.map((image, index) => {
                        const dimensions = photoDimensions[
                          image.image_path
                        ] || {
                          width: 200,
                          height: 200,
                        };
                        return {
                          src: bucketUrlPrefix + image.image_path,
                          width: dimensions.width,
                          height: dimensions.height,
                          alt:
                            image.alt_text ||
                            `${block.title || tab.title} image ${index + 1}`,
                        };
                      })}
                      spacing={8}
                      targetRowHeight={200}
                      rowConstraints={(containerWidth) => {
                        let maxPhotos = 2;
                        if (containerWidth > 1024) {
                          maxPhotos = 4;
                        } else if (containerWidth > 768) {
                          maxPhotos = 3;
                        }
                        return {
                          maxPhotos,
                          minPhotos: 1,
                          singleRowMaxHeight: 350,
                        };
                      }}
                      onClick={({ index }) => {
                        handleOpenFullImage(blockImages[index].image_path);
                      }}
                      render={{
                        button: (props, context) => {
                          const { index } = context;
                          const img = blockImages[index];
                          const altText = img.alt_text || "";
                          const ariaLabel = altText
                            ? `Photo ${index + 1} of ${blockImages.length}. ${altText}`
                            : `Photo ${index + 1} of ${blockImages.length}`;

                          return (
                            <button
                              {...props}
                              aria-label={ariaLabel}
                              className={`${props.className} focus-blue rounded relative overflow-hidden group`}
                              style={{
                                ...props.style,
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleOpenFullImage(img.image_path);
                                }
                              }}
                            >
                              {props.children}
                              <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded" />
                            </button>
                          );
                        },
                      }}
                    />
                  </div>
                )}
              </BlockTag>
            );
          })}

          {/* Full image view spans every block's images so prev/next walks the whole tab */}
          {fullImageView &&
            currentImage &&
            allImages.some((img) => img.image_path === currentImage) && (
              <FullImageViewer
                images={allImages.map((image, index) => ({
                  src: bucketUrlPrefix + image.image_path,
                  alt: image.alt_text || `${tab.title} image ${index + 1}`,
                  tag_name: tab.title,
                  category_name: tab.title,
                  created_at: null,
                  photo_type: "tab",
                  review_photos_id: null,
                  width: photoDimensions[image.image_path]?.width || 800,
                  height: photoDimensions[image.image_path]?.height || 600,
                  sort_order: image.order,
                  qtags: [],
                  tags: [
                    {
                      tagName: tab.title,
                      categoryDisplaySectionId: "",
                    },
                  ],
                  displaySections: {},
                  venueId: venueDetails.business.id,
                  venueName: venueDetails.name,
                  venueAddress: venueDetails.address,
                  venueBrandId: venueDetails.brandId,
                  venueBrandName: venueDetails.brandName,
                  venueTypes: venueDetails.businessType,
                }))}
                currentIndex={allImages.findIndex(
                  (img) => img.image_path === currentImage,
                )}
                onClose={() => setFullImageView(false)}
                onFlag={() => {}}
                showFlag={false}
                showRightTab={false}
              />
            )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Show loader when syncing interest */}
      {isSyncingInterest && (
        <Loader aria-label="Updating notification preferences" />
      )}

      {/* Accessibility Alerts Page - Full Responsive Layout */}
      {/* Mobile-first design with lg: breakpoints for desktop 3-column layout */}
      {/* VENUE CARD and Tab Navigation are static, only TAB CONTENT scrolls */}

      <div
        ref={pageScrollRef}
        className="bg-bg-brand-secondary-default h-full flex flex-col overflow-y-auto scrollbar-hide lg:overflow-hidden"
      >
        {/* ===== MAIN CONTENT ===== */}
        <div className="flex-1 flex flex-col lg:overflow-y-hidden p-4 md:p-0 md:pl-4 md:pb-4">
          {/* Back Navigation (Mobile) */}
          {!isDesktopLayout && (
            <div className="flex lg:hidden px-2 shrink-0">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 py-4 group cursor-pointer"
              >
                <ArrowLeftIcon
                  size={20}
                  aria-hidden="true"
                  className="!text-fg-brand-subtle"
                />
                <p className="ml-1 label-m-semibold !text-fg-brand-subtle">
                  {backLabel}
                </p>
              </button>
            </div>
          )}

          {/* Content Area - Responsive Grid */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden gap-4">
            {/* ===== LEFT COLUMN (Desktop only) ===== */}
            {isDesktopLayout && (
            <div className="hidden lg:flex lg:flex-col lg:w-[290px] lg:shrink-0 bg-bg-brand-secondary-default">
              {/* Back Button - Desktop Left Column.
                  h-14 matches the tabs row so the two underlines read as one
                  rule; the negative margin carries the border across the
                  column gap, which a border alone cannot span. */}
              <div className="h-14 flex items-center border-b border-border-token-brand-minimal shrink-0 bg-bg-brand-secondary-default -mr-4 pr-4">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 group cursor-pointer"
                >
                  <ArrowLeftIcon
                    size={20}
                    aria-hidden="true"
                    className="!text-fg-brand-subtle"
                  />
                  <p className="ml-1 label-m-semibold !text-fg-brand-subtle">
                    {backLabel}
                  </p>
                </button>
              </div>

              {/* Sidebar - Desktop Left Column */}
              <article className="flex-1 flex flex-col gap-4 overflow-y-auto md:mt-4">
                {/* Venue Image with Action Buttons */}
                <VenueImageWithActions
                  businessPhoto={businessPhoto}
                  businessphotoThumb={businessphotoThumb}
                  venueName={venueDetails.name}
                  venueSlug={venueDetails.business.slug}
                  hasVenuePhoto={hasVenuePhoto}
                  isVenueSaved={isVenueSaved}
                  handleSaveVenue={handleSavePlaceCta}
                  isLoggedIn={isLoggedIn}
                  setActionToPerformAfterLogin={setActionToPerformAfterLogin}
                  isDark={isDark}
                  onNotificationClick={handleNotificationClick}
                  isNotificationEnabled={isNotificationEnabled}
                  // Same picker as Contribute — it carries the QR panel.
                  onAddPhotos={() => setIsContributeOpen(true)}
                  venueDetails={venueDetails}
                  variant="desktop"
                />

                {/* Venue Details - Desktop only */}
                <div className="flex flex-col">
                  {/* Group 1: Name + Location (tightly grouped) */}
                  <h1 className="title-m-bold text-fg-neutral-strong">
                    {venueDetails.name}
                  </h1>
                  {(locationOnly || venueDetails.businessType) && (
                    <div className="flex items-baseline flex-wrap gap-x-1 mt-3">
                      {locationOnly && (
                        <p className="label-s-medium text-fg-neutral-subtle">
                          {locationOnly}
                          {venueDetails.businessType && (
                            <span className="text-fg-neutral-subtle">
                              {" "}
                              •
                            </span>
                          )}
                        </p>
                      )}
                      {venueDetails.businessType && (
                        <p className="label-s-medium text-fg-neutral-subtle">
                          {venueDetails.businessType}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Distance + address as one directions link (Figma
                      16437:72833), then the hours on their own line. 4px
                      below the place line, vs 12px under the title — the meta
                      rows are one visual group in Figma (node 15003-54105).
                      The distance is left out until it is measured from the
                      user's own location. */}
                  <DirectionsLink
                    venueName={venueDetails.name}
                    latitude={venueDetails.latitude}
                    longitude={venueDetails.longitude}
                    placeId={venueDetails.placeId}
                    distanceLabel={
                      showDistance ? `${venueDetails.distance} mile` : undefined
                    }
                    address={venueDetails.address}
                    className="mt-1"
                  />
                  {isTemporarilyClosed ? (
                    <p className="label-s-medium text-fg-error-subtle mt-1">
                      {i18n.get("temporarilyClosed")}
                    </p>
                  ) : (
                    showTodaysHours && (
                      // Opening Hours (arrow focusable, if present)
                      <p className="label-s-medium text-fg-neutral-subtle mt-1">
                        <OpeningHours
                          openingHours={venueDetails.openingHours}
                          containerClassName=""
                          className="label-s-medium text-fg-neutral-subtle"
                        />
                      </p>
                    )
                  )}
                </div>

                {showAboutBlock && (
                  <VenueAboutBlock
                    venueDetails={venueDetails}
                  />
                )}

                {/* The alerts callout that used to sit here is gone — the
                    stats row's "Access alerts" cell already carries the count,
                    the red treatment and the jump to the alerts tab. The AI
                    summary and verified/contributed line that used to sit
                    here too now live in AccessibilitySummaryCard, at the top
                    of the Accessibility tab, matching the native app. */}

                {/* mt-2 tops up the article's own gap-4 (16px) to the 24px
                    Figma spec for this specific gap (node 15003-54105). */}
                <div className="flex flex-col mt-2">
                  {/* At-a-glance counts — each cell jumps to its tab. */}
                  <VenueStatsRow
                    alertCount={accessibilityAlerts.length}
                    // Same count as the Photos tab badge, so the two agree.
                    photoCount={countVenuePhotos(venueDetails?.businessPhotos)}
                    venueDetails={venueDetails}
                    reviewCount={reviews.length}
                    onSelectTab={selectMainTab}
                  />

                  {/* Contribute Information Button — opens the shared picker
                      rather than jumping straight into the review form. */}
                  <button
                    onClick={() => {
                      trackContributeCta("place_landing");
                      setIsContributeOpen(true);
                    }}
                    className="mt-6 flex items-center justify-center gap-2 w-full h-10 bg-bg-brand-primary-default
                    rounded-lg label-m-semibold hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active
                    transition-all duration-200 focus-blue"
                  >
                    <PlusCircleIcon
                      size={20}
                      aria-hidden="true"
                      className="text-fg-neutral-onFilled"
                    />
                    <span className="label-m-medium text-fg-neutral-onFilled">
                      {i18n.get("contribute")}
                    </span>
                  </button>

                  {/* Claim Business Link */}
                  <div className="mt-3">
                    <VenueInfoClaim
                      venueDetailsResponse={venueDetailsResponse}
                      onRefresh={onRefresh}
                    />
                  </div>
                </div>
              </article>
            </div>
            )}

            {/* Both portal to body, so they sit outside the per-layout columns. */}
            {isContributeOpen && (
              <ContributionOptionsModal
                entryPoint="place_landing"
                analyticsVenue={{
                  brand_id:
                    venueDetails.brandId != null
                      ? String(venueDetails.brandId)
                      : "",
                  brand_name: venueDetails.brandName ?? "",
                  business_id: venueDetails.business.id.toString(),
                  place_name: venueDetails.name,
                }}
                onSelectAction={handleContributionAction}
                onClose={() => setIsContributeOpen(false)}
              />
            )}

            <AddReviewModal
              venueId={venueDetails.business.id.toString()}
              venueName={venueDetails.name}
              isOpen={isAddReviewOpen}
              onClose={handleAddReviewClose}
              venueDetails={venueDetails}
              review={reviewToEdit}
              onSuccess={() => setSelectedMainTab("reviews")}
            />

            {/* ===== RIGHT COLUMN (All viewports) ===== */}
            <div className="flex-1 flex flex-col overflow-hidden bg-bg-brand-secondary-default">
              {/* Mobile-only article */}
              {!isDesktopLayout && (
              <article className="lg:hidden w-full flex flex-col gap-2 overflow-y-auto md:pr-0">
                {/* Venue Image with Action Buttons */}
                <VenueImageWithActions
                  businessPhoto={businessPhoto}
                  businessphotoThumb={businessphotoThumb}
                  venueName={venueDetails.name}
                  venueSlug={venueDetails.business.slug}
                  hasVenuePhoto={hasVenuePhoto}
                  isVenueSaved={isVenueSaved}
                  handleSaveVenue={handleSavePlaceCta}
                  isLoggedIn={isLoggedIn}
                  setActionToPerformAfterLogin={setActionToPerformAfterLogin}
                  isDark={isDark}
                  onNotificationClick={handleNotificationClick}
                  isNotificationEnabled={isNotificationEnabled}
                  onAddPhotos={() => setIsContributeOpen(true)}
                  venueDetails={venueDetails}
                  variant="mobile"
                  showDistance={showDistance}
                />

                {showAboutBlock && (
                  <VenueAboutBlock
                    venueDetails={venueDetails}
                    className="mt-4"
                  />
                )}

                {/* Venue Details - Mobile only. mt-4 tops up the mobile
                    article's own gap-2 (8px) to the 24px Figma spec for
                    this gap (node 15003-54105). */}
                <div className="flex flex-col gap-2 mt-4">
                  {/* At-a-glance counts — same component as desktop. */}
                  <VenueStatsRow
                    alertCount={accessibilityAlerts.length}
                    photoCount={countVenuePhotos(venueDetails?.businessPhotos)}
                    venueDetails={venueDetails}
                    reviewCount={reviews.length}
                    onSelectTab={selectMainTab}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  {/* Contribute Information Button — same picker as desktop;
                      the modal renders full-screen below lg. */}
                  <button
                    onClick={() => {
                      trackContributeCta("place_landing");
                      setIsContributeOpen(true);
                    }}
                    className="mt-4 mb-2 flex items-center justify-center gap-2 w-full h-10 bg-bg-brand-primary-default
                    rounded-lg label-m-semibold hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active
                    transition-all duration-200 focus-blue
                    min-h-[44px]"
                  >
                    <PlusCircleIcon
                      size={20}
                      aria-hidden="true"
                      className="text-fg-neutral-onFilled"
                    />
                    <span className="label-m-medium text-fg-neutral-onFilled">
                      {i18n.get("contribute")}
                    </span>
                  </button>

                  {/* Verified/contributed line — mobile only; on desktop this
                      lives inside the Accessibility tab's summary card instead. */}
                  <VenueDataSourceBadge
                    venue={venueDetails}
                    className="flex items-center justify-center gap-1"
                    labelClassName="label-m-semibold text-fg-neutral-strong whitespace-nowrap"
                    withInfo
                  />

                  {/* Claim Business Link */}
                  <VenueInfoClaim
                    venueDetailsResponse={venueDetailsResponse}
                    onRefresh={onRefresh}
                  />

                  {/* Ask AI teaser — mobile only (hidden at md+ where the docked panel lives) */}
                  {chatAssistantEnabled && (
                    <button
                      className="md:hidden w-full flex items-center gap-3 p-3 rounded-xl bg-container-brand-minimal border border-border-token-brand-minimal hover:bg-bg-brand-ghost-hover transition-colors text-left mt-1"
                      onClick={() => setIsAssistantOverlayOpen(true)}
                      aria-label={i18n.get("askAiAboutThisVenue")}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-bg-brand-primary-default shrink-0">
                        <SparkleIcon
                          size={20}
                          weight="fill"
                          aria-hidden="true"
                          className="text-fg-neutral-onFilled"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="label-m-semibold text-fg-neutral-strong">
                          {i18n.get("askAiAboutThisVenue")}
                        </p>
                        <p className="label-s-medium text-fg-neutral-subtle">
                          {i18n.get("askAiTeaserSubtitle")}
                        </p>
                      </div>
                      <CaretRightIcon
                        size={18}
                        aria-hidden="true"
                        className="text-fg-neutral-subtle shrink-0"
                      />
                    </button>
                  )}
                </div>
              </article>
              )}

              {/* Desktop Tabs */}
              {/* items-stretch, not items-center: the tabs must fill the row's
                  full height so the active tab's underline lands on the
                  divider instead of floating above it. */}
              {isDesktopLayout && (
              <div className="hidden lg:flex h-14 items-stretch border-b border-border-token-brand-minimal shrink-0">
                <ScrollContainer className="flex-1 min-w-0 h-full">
                  <div
                    className="flex items-stretch w-max min-w-full h-full"
                    role="tablist"
                    aria-label={i18n.get("venueDetailsTabs")}
                  >
                    {mainTabs.map((tab, index) => (
                      <button
                        key={tab}
                        ref={(el) => {
                          if (el) desktopTabRefs.current[index] = el;
                        }}
                        role="tab"
                        aria-selected={selectedMainTab === tab}
                        aria-controls={`tabpanel-${tab}`}
                        tabIndex={selectedMainTab === tab ? 0 : -1}
                        id={`desktop-tab-${tab}`}
                        onClick={() => {
                          selectMainTab(tab);
                          handleTabTracking(tab);
                        }}
                        onKeyDown={(e) =>
                          handleTabKeyDown(e, index, desktopTabRefs)
                        }
                        className={cn(
                          "relative flex items-center justify-center px-6 whitespace-nowrap min-w-[44px] min-h-[44px] transition-colors flex-shrink-0 bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover",
                          selectedMainTab === tab
                            ? "label-m-semibold text-fg-brand-subtle"
                            : "label-m-medium text-fg-neutral-subtle",
                        )}
                      >
                        <span className="flex items-center gap-1.5">
                          {mainTabItems[index].label}
                          {mainTabItems[index].badgeCount > 0 && (
                            <span
                              className={cn(
                                "flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5 label-s-medium",
                                selectedMainTab === tab
                                  ? "bg-container-brand-subtle text-fg-neutral-onFilled"
                                  : "bg-container-neutral-subtle text-fg-neutral-onFilled",
                              )}
                            >
                              <span aria-hidden="true">
                                {mainTabItems[index].badgeCount > 99 ? "99+" : mainTabItems[index].badgeCount}
                              </span>
                              <span className="sr-only">{`${mainTabItems[index].badgeCount} ${i18n.get("items")}`}</span>
                            </span>
                          )}
                        </span>
                        {selectedMainTab === tab && (
                          <span
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-bg-brand-primary-default"
                            aria-hidden="true"
                          ></span>
                        )}
                      </button>
                    ))}
                  </div>
                </ScrollContainer>

                {/* Ask AI button — desktop (lg+), opens the docked chat panel */}
                {chatAssistantEnabled && (
                  <div className="shrink-0 flex items-center px-3 h-full">
                    <button
                      onClick={openAssistant}
                      aria-expanded={isAssistantDockOpen}
                      aria-controls="venue-assistant-dock"
                      className="flex items-center gap-1.5 px-4 h-9 rounded-lg bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active transition-colors label-m-semibold text-fg-neutral-onFilled"
                    >
                      <SparkleIcon
                        size={16}
                        weight="fill"
                        aria-hidden="true"
                      />
                      <span>{i18n.get("askAi")}</span>
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Mobile Tabs */}
              {!isDesktopLayout && (
              <div
                ref={mobileTabBarRef}
                className="lg:hidden flex items-center border-b border-border-token-brand-minimal shrink-0"
              >
                <ScrollContainer className="flex-1 min-w-0">
                  <div
                    className="flex items-center w-max min-w-full"
                    role="tablist"
                    aria-label={i18n.get("venueDetailsTabs")}
                  >
                    {mainTabs.map((tab, index) => (
                      <button
                        key={tab}
                        ref={(el) => {
                          if (el) mobileTabRefs.current[index] = el;
                        }}
                        role="tab"
                        aria-selected={selectedMainTab === tab}
                        aria-controls={`tabpanel-${tab}`}
                        tabIndex={selectedMainTab === tab ? 0 : -1}
                        id={`mobile-tab-${tab}`}
                        onClick={() => {
                          selectMainTab(tab);
                          handleTabTracking(tab);
                        }}
                        onKeyDown={(e) =>
                          handleTabKeyDown(e, index, mobileTabRefs)
                        }
                        className={cn(
                          "relative px-6 whitespace-nowrap min-w-[44px] min-h-[44px] transition-colors flex-shrink-0 bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover",
                          selectedMainTab === tab
                            ? "label-m-semibold text-fg-brand-subtle"
                            : "label-m-medium text-fg-neutral-subtle",
                        )}
                      >
                        <span className="flex items-center gap-1.5">
                          {mainTabItems[index].label}
                          {mainTabItems[index].badgeCount > 0 && (
                            <span
                              className={cn(
                                "flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5 label-s-medium",
                                selectedMainTab === tab
                                  ? "bg-container-brand-subtle text-fg-neutral-onFilled"
                                  : "bg-container-neutral-subtle text-fg-neutral-onFilled",
                              )}
                            >
                              <span aria-hidden="true">
                                {mainTabItems[index].badgeCount > 99 ? "99+" : mainTabItems[index].badgeCount}
                              </span>
                              <span className="sr-only">{`${mainTabItems[index].badgeCount} ${i18n.get("items")}`}</span>
                            </span>
                          )}
                        </span>
                        {selectedMainTab === tab && (
                          <span
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-bg-brand-primary-default"
                            aria-hidden="true"
                          ></span>
                        )}
                      </button>
                    ))}
                  </div>
                </ScrollContainer>

                {/* Ask AI button — tablet (md to lg), opens the docked chat panel */}
                {chatAssistantEnabled && (
                  <div className="hidden md:flex shrink-0 items-center px-3 h-full">
                    <button
                      onClick={openAssistant}
                      aria-expanded={isAssistantDockOpen}
                      aria-controls="venue-assistant-dock"
                      className="flex items-center gap-1.5 px-4 h-9 rounded-lg bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active transition-colors label-m-semibold text-fg-neutral-onFilled"
                    >
                      <SparkleIcon
                        size={16}
                        weight="fill"
                        aria-hidden="true"
                      />
                      <span>{i18n.get("askAi")}</span>
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Tab Panel Content */}
              {/* Below lg the whole page scrolls and the tab bar sits ~750px
                  down, so a short tab (an empty state) must still leave a
                  screenful beneath it — without this the browser clamps the
                  scroll that brings the tab bar to the top and the tab looks
                  like it never scrolled. At lg+ the panel scrolls internally,
                  where the same min-height would force a needless scrollbar. */}
              <div className="flex-1 flex flex-col w-full min-h-[calc(100vh-140px)] lg:min-h-0 lg:p-0 lg:mt-4  md:pr-0">
                {/* Main scrollable container with border */}
                <div
                  ref={tabContentScrollRef}
                  className="flex-1 overflow-y-auto md:border [scrollbar-gutter:stable_both-edges]
                  md:border-border-token-brand-minimal md:rounded-xl bg-bg-brand-secondary-default"
                >
                  {/* [scrollbar-gutter:stable_both-edges] */}
                  {/* Tab Content Section */}
                  <section
                    className="flex flex-col md:p-4 min-h-full"
                    role="tabpanel"
                    id={`tabpanel-${selectedMainTab}`}
                    aria-labelledby={`${isDesktopLayout ? "desktop" : "mobile"}-tab-${selectedMainTab}`}
                    tabIndex={0}
                  >
                    {/* Accessibility BASIC */}
                    {selectedMainTab === "accessibility" &&
                      (venueDetails.templateType === "Basic" ||
                        venueDetails.templateType === "") && (
                        <AccessibilitySection
                          venueDetailsResponse={venueDetailsResponse}
                          fullSize={fullSize}
                          initialSection={_initialSection}
                          onLogin={handleShowLoginModal}
                          onRefresh={onRefresh}
                          cid={tb === "photo" ? undefined : cid}
                          tid={tb === "photo" ? undefined : tid}
                          onShowDetailView={onShowDetailView}
                          onNotificationClick={() =>
                            handleNotificationClick("accessibility")
                          }
                          isNotificationEnabled={
                            isAccessibilityInfoNotificationEnabled
                          }
                          onOpenAssistant={openAssistantAnywhere}
                        />
                      )}

                    {selectedMainTab === "photos" && (
                      <PhotosTab
                        venueDetailsResponse={venueDetailsResponse}
                        fullSize={fullSize}
                        openAllPhotosInline={openAllPhotosInline}
                        top5Photos={top5Photos}
                        cid={cid}
                        tid={tid}
                        // onRefresh={onRefresh}
                        onRefresh={() => {}}
                        onShowPhotosDetailView={onShowPhotosDetailView}
                      />
                    )}

                    {selectedMainTab === "alerts" && (
                      <AlertsTab
                        venueDetailsResponse={venueDetailsResponse}
                        onRefresh={onRefresh}
                        showMapperView={showMapperView}
                        onEdit={showEditNote}
                        onFlagNote={showFlagNote}
                        onNotificationClick={() =>
                          handleNotificationClick("liveAlerts")
                        }
                        isNotificationEnabled={isLiveAlertNotificationEnabled}
                      />
                    )}

                    {selectedMainTab === "reviews" && (
                      <ReviewsTab
                        reviews={reviews}
                        fullSize={fullSize}
                        onFlag={(review) => {
                          if (!user || !isLoggedIn) {
                            setActionToPerformAfterLogin("flagReview");
                            setActionToPerformValueAfterLogin(review);
                            handleShowLoginModal();
                            return;
                          }
                          // Flagging a review/experience → flag_experience_form_opened only.
                          // (The plan's `flag_action_button` element also fans out to
                          // alert_flag_cta_activated, which needs alert_title and is for alert
                          // flagging — not this surface — so use the trackEvent primitive, matching
                          // the flag_experience_form_closed wiring in handleFlagVenueReviewClose.)
                          trackEvent("flag_experience_form_opened", {
                            business_id: venueDetails.business.id.toString(),
                            brand_id: venueDetails.brandId != null ? String(venueDetails.brandId) : "",
                            brand_name: venueDetails.brandName ?? "",
                            experience_type: experienceTypeForAnalytics(
                              review?.accessibility_experience,
                            ),
                            place_name: venueDetails.name,
                          });
                          setSelectedReview(review);
                          setIsFlagReviewOpen(true);
                        }}
                        isLoggedIn={isLoggedIn}
                        onShowLoginModal={handleShowLoginModal}
                        venueDetails={venueDetails}
                        currentReviewSort={currentReviewSort}
                        onReviewSort={handleReviewSort}
                        sortByText={sortByText}
                        currentUserId={user?.id}
                        onEditReview={(review) => {
                          setReviewToEdit(review);
                          setIsAddReviewOpen(true);
                        }}
                        onDeleteReview={(review) => setReviewToDelete(review)}
                      />
                    )}

                    {selectedMainTab === "about" && (
                      <AboutTab
                        venueDetailsResponse={venueDetailsResponse}
                        onRefresh={onRefresh}
                      />
                    )}

                    {/* Dynamic Tabs */}
                    {selectedMainTab.startsWith("dynamicTab_") &&
                      renderDynamicTabContent(selectedMainTab)}

                    {/* Empty state when no tab content is available */}
                    {!hasAccessibilityData &&
                      !hasPhotos &&
                      !hasAlerts &&
                      !hasReviews &&
                      selectedMainTab === "accessibility" && (
                        <div className="flex flex-col items-center justify-center gap-2 max-w-[312px] text-center mx-auto">
                          <h2
                            id="tab-content-title"
                            className="label-m-semibold text-fg-neutral-strong"
                          >
                            {i18n.get("wantMoreAccessibilityDetails")}
                          </h2>
                          <p className="body-s-regular text-fg-neutral-subtle">
                            {i18n.get("clickToBeNotifiedWhenThisVenueHas")}
                          </p>
                          <button
                            className="flex items-center justify-center gap-2 w-full mt-2 px-4 py-2 border border-border-token-brand-minimal rounded-lg label-m-semibold text-fg-neutral-strong hover:bg-bg-brand-ghost-hover transition-colors min-h-[44px]"
                            aria-label="Get notifications for accessibility updates"
                            onClick={() => {
                              // Empty-state "I want to know" CTA — fire its source element here
                              // (it has no child component to own the event, unlike the bell /
                              // accessibility / live-alerts CTAs).
                              createPageAnalytics("place").track(
                                "I_want_to_know_link_button",
                                {
                                  business_id:
                                    venueDetails.business.id.toString(),
                                  brand_id: venueDetails.brandId != null ? String(venueDetails.brandId) : "",
                                  brand_name: venueDetails.brandName ?? "",
                                  pin_type:
                                    pinTypeForVenueDetails(venueDetails),
                                  place_name: venueDetails.name,
                                },
                              );
                              handleNotificationClick();
                            }}
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                              aria-hidden="true"
                              focusable="false"
                            >
                              <path d="M8 14.5c.83 0 1.5-.67 1.5-1.5h-3c0 .83.67 1.5 1.5 1.5zm5-4V7c0-2.48-1.51-4.5-4-5.04V1.5c0-.55-.45-1-1-1s-1 .45-1 1v.46C4.51 2.5 3 4.52 3 7v3.5L1.5 12v.5h13V12L13 10.5z" />
                            </svg>
                            {i18n.get("iWantToKnow")}
                          </button>
                        </div>
                      )}
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Places Modal */}
      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          ariaLabelledby="save-place-dialog-title"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <span id="save-place-dialog-title" hidden>{i18n.get("saveToList")}</span>
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}

      {/* DEV-544 — delete-your-own-experience confirmation. Soft delete server-side,
          but irreversible from the product, so it asks first. */}
      <ModalWithAction
        isModalOpen={!!reviewToDelete}
        title={i18n.get("deleteExperience")}
        subtitle={i18n.get("deleteExperienceConfirmation")}
        cancelButtonTitle={i18n.get("cancel")}
        actionButtonTitle={i18n.get("delete")}
        actionButtonType="error"
        type="error"
        onClose={() => setReviewToDelete(null)}
        onClick={handleDeleteExperience}
      />

      {/* Flag Review Modal */}
      <FlagVenueReview
        review={selectedReview}
        venueName={venueDetails.name}
        isOpen={isFlagReviewOpen}
        onClose={handleFlagVenueReviewClose}
        venueDetails={venueDetails}
      />

      {/* Edit Venue Note Modal */}
      {editVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={editVenueNoteOpenModal}
          onClose={() => setEditVenueNoteOpenModal(false)}
          closeOnOverlayClick={false}
          ariaLabelledby="edit-note-dialog-title"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <span id="edit-note-dialog-title" hidden>{i18n.get("editNote")}</span>
          <EditVenueNote
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => setEditVenueNoteOpenModal(false)}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
            note={editNote}
          />
        </Modal>
      )}

      {/* Flag Venue Note Modal */}
      {flagVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={flagVenueNoteOpenModal}
          onClose={() => setFlagVenueNoteOpenModal(false)}
          ariaLabelledby="flag-note-dialog-title"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <span id="flag-note-dialog-title" hidden>{i18n.get("flagNote")}</span>
          <FlagVenueNote
            venueDetails={venueDetails}
            onClose={() => setFlagVenueNoteOpenModal(false)}
            note={flagNote}
          />
        </Modal>
      )}

      {/* Add Mapper Note Modal */}
      {addNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addNoteOpenModal}
          onClose={() => handleAddNoteModalClose()}
          ariaLabelledby="add-note-dialog-title"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <span id="add-note-dialog-title" hidden>{i18n.get("addNote")}</span>
          <AddMapperNote
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => handleAddNoteModalClose()}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
          />
        </Modal>
      )}

      {/* Area Details Notes Modal */}
      {areaDetailsOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={false}
          animationDuration={0}
          open={areaDetailsOpenModal}
          onClose={() => setAreaDetailsOpenModal(false)}
          ariaLabelledby="area-details-dialog-title"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <span id="area-details-dialog-title" hidden>{i18n.get("areaDetailsNotes")}</span>
          <AreaDetailsNotes
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => {
              setAreaDetailsOpenModal(false);
            }}
            mode="mappernote"
          />
        </Modal>
      )}

      {/* Interest Notification Modal */}
      <InterestNotificationModal
        businessId={venueDetails.business.id.toString()}
        businessName={venueDetails.name}
        brandId={venueDetails.brandId != null ? String(venueDetails.brandId) : ""}
        brandName={venueDetails.brandName ?? ""}
        interestId={currentUserInterest?.id?.toString()}
        interestedType={currentUserInterest?.interested_type}
        notifyAccessibilityInformation={
          preSelectedNotificationType === "accessibility"
            ? true
            : currentUserInterest?.notify_accessibility_information === 1
        }
        notifyLiveAlert={
          preSelectedNotificationType === "liveAlerts"
            ? true
            : currentUserInterest?.notify_live_alert === 1
        }
        isOpen={isNotificationModalOpen}
        onClose={handleNotificationModalClose}
        onSuccess={handleNotificationModalSuccess}
        onComplete={handleNotificationModalComplete}
        alertsCount={
          (venueDetails as { businessNotesAll?: unknown[] }).businessNotesAll
            ?.length ?? 0
        }
        skipSuccessModal={!!preSelectedNotificationType}
        // skipSuccessModal={false}
      />

      {/* Full-screen Ask AI overlay — mobile everywhere, plus the desktop/tablet
          fallback when no docked panel is provided (search, saved places, nearby). */}
      {chatAssistantEnabled && isAssistantOverlayOpen && (
        <FocusTrap focusTrapOptions={{ onDeactivate: () => setIsAssistantOverlayOpen(false) }}>
        <div role="dialog" aria-modal="true" aria-label={i18n.get("askAiAboutThisVenue")} className="fixed inset-0 z-[9999] bg-bg-brand-secondary-default flex flex-col p-4">
          <VenueAssistantTab
            venueSlug={venueDetails.business.slug}
            venueName={venueDetails.name}
            onClose={() => setIsAssistantOverlayOpen(false)}
            closeVariant="back"
            hasAccessibilityData={hasAccessibilityData}
            hasOpeningHours={(venueDetails?.openingHours?.length ?? 0) > 0}
          />
        </div>
        </FocusTrap>
      )}
    </>
  );
}

export default VenueSheetContent;
